import { useCallback, useEffect, useRef, useState } from 'react'
import { Menu, X } from 'lucide-react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/useAuth'
import AlertPanel from './AlertPanel'
import styles from './Layout.module.css'

const MANUAL_OPERATING_ENABLED =
  import.meta.env.VITE_MANUAL_OPERATING_ENABLED === 'true'
const COMPACT_VIEWPORT_QUERY = '(max-width: 899px)'
const SIDEBAR_ID = 'primary-navigation-drawer'

type CloseReason = 'dismiss' | 'navigation' | 'viewport'
type FocusTarget = 'trigger' | 'main' | null

function useCompactViewport() {
  const [isCompact, setIsCompact] = useState(() =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(COMPACT_VIEWPORT_QUERY).matches
      : false,
  )

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return

    const mediaQuery = window.matchMedia(COMPACT_VIEWPORT_QUERY)
    const handleChange = (event: MediaQueryListEvent) => {
      setIsCompact(event.matches)
    }

    setIsCompact(mediaQuery.matches)
    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [])

  return isCompact
}

function focusableElements(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hasAttribute('hidden'))
}

export function guardianAlphaNavigationItems(manualOperatingEnabled: boolean) {
  return [
    ...(manualOperatingEnabled
      ? [{ to: '/guardian-alpha/manual-operating', label: 'Manual Operating' }]
      : []),
    { to: '/guardian-alpha/config', label: 'Config' },
  ]
}

export default function Layout() {
  const { user, logout, hasPermission } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const dashboardVersion = __APP_VERSION__
  const isCompact = useCompactViewport()
  const [serverVersion, setServerVersion] = useState<string | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)
  const sidebarTriggerRef = useRef<HTMLButtonElement>(null)
  const sidebarCloseRef = useRef<HTMLButtonElement>(null)
  const contentShellRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLElement>(null)
  const pendingFocusRef = useRef<FocusTarget>(null)
  const previousPathnameRef = useRef(location.pathname)

  useEffect(() => {
    let cancelled = false
    fetch('/health/liveness')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.version) setServerVersion(data.version)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const closeSidebar = useCallback((reason: CloseReason) => {
    pendingFocusRef.current =
      reason === 'dismiss' ? 'trigger' : reason === 'navigation' ? 'main' : null
    setIsSidebarOpen(false)
  }, [])

  useEffect(() => {
    const sidebar = sidebarRef.current
    if (!sidebar) return

    if (isCompact && !isSidebarOpen) {
      sidebar.setAttribute('inert', '')
    } else {
      sidebar.removeAttribute('inert')
    }

    return () => sidebar.removeAttribute('inert')
  }, [isCompact, isSidebarOpen])

  useEffect(() => {
    if (!isCompact || !isSidebarOpen) return

    const sidebar = sidebarRef.current
    const contentShell = contentShellRef.current
    if (!sidebar || !contentShell) return

    const previousOverflow = document.body.style.overflow
    contentShell.setAttribute('inert', '')
    document.body.style.overflow = 'hidden'
    sidebarCloseRef.current?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        closeSidebar('dismiss')
        return
      }

      if (event.key !== 'Tab' || !sidebar) return
      const focusable = focusableElements(sidebar)
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const activeElement = document.activeElement

      if (event.shiftKey && (activeElement === first || !sidebar.contains(activeElement))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && (activeElement === last || !sidebar.contains(activeElement))) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      contentShell.removeAttribute('inert')
      document.body.style.overflow = previousOverflow
    }
  }, [closeSidebar, isCompact, isSidebarOpen])

  useEffect(() => {
    if (isSidebarOpen || pendingFocusRef.current === null) return

    const focusTarget = pendingFocusRef.current
    pendingFocusRef.current = null
    if (focusTarget === 'trigger') {
      sidebarTriggerRef.current?.focus()
    } else {
      mainRef.current?.focus()
    }
  }, [isSidebarOpen])

  useEffect(() => {
    if (!isCompact && isSidebarOpen) {
      closeSidebar('viewport')
    }
  }, [closeSidebar, isCompact, isSidebarOpen])

  useEffect(() => {
    if (previousPathnameRef.current === location.pathname) return

    previousPathnameRef.current = location.pathname
    if (isSidebarOpen) {
      closeSidebar('navigation')
    }
  }, [closeSidebar, isSidebarOpen, location.pathname])

  function handleLogout() {
    pendingFocusRef.current = null
    setIsSidebarOpen(false)
    logout()
    navigate('/login', { replace: true })
  }

  const nodeManagerItems = [
    ...(hasPermission('node:read')
      ? [
          { to: '/nodes', label: 'Nodes' },
          { to: '/jobs', label: 'Jobs' },
          { to: '/server', label: 'Server' },
        ]
      : []),
    ...(hasPermission('config:read') ? [{ to: '/config', label: 'Config' }] : []),
    ...(hasPermission('node:read') ? [{ to: '/deployment', label: 'Deployment' }] : []),
    ...(hasPermission('secret:manage') ? [{ to: '/secrets', label: 'Secrets' }] : []),
    ...(hasPermission('agent-release:manage') ? [{ to: '/releases', label: 'Releases' }] : []),
    ...(hasPermission('user:manage') ? [{ to: '/users', label: 'Users' }] : []),
    ...(hasPermission('audit:read') ? [{ to: '/audit-logs', label: 'Audit Logs' }] : []),
  ]
  const guardianAlphaItems = hasPermission('config:read')
    ? guardianAlphaNavigationItems(MANUAL_OPERATING_ENABLED)
    : []

  return (
    <div className={styles.layout}>
      {isCompact && isSidebarOpen && (
        <div
          className={styles.backdrop}
          data-testid="navigation-backdrop"
          onClick={() => closeSidebar('dismiss')}
        />
      )}

      <aside
        id={SIDEBAR_ID}
        ref={sidebarRef}
        className={[
          styles.sidebar,
          isCompact && isSidebarOpen ? styles.sidebarOpen : '',
        ].filter(Boolean).join(' ')}
        aria-hidden={isCompact && !isSidebarOpen ? true : undefined}
        aria-label={isCompact && isSidebarOpen ? 'Navigation menu' : undefined}
        aria-modal={isCompact && isSidebarOpen ? true : undefined}
        role={isCompact && isSidebarOpen ? 'dialog' : undefined}
      >
        <nav aria-label="Primary navigation" className={styles.sidebarNavigation}>
          <div className={styles.brandRow}>
            <div className={styles.brand}>Node Manager</div>
            {isCompact && (
              <button
                ref={sidebarCloseRef}
                type="button"
                className={styles.closeButton}
                aria-label="Close navigation"
                onClick={() => closeSidebar('dismiss')}
              >
                <X size={20} aria-hidden="true" />
              </button>
            )}
          </div>

          <div className={styles.navigationItems}>
            {nodeManagerItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  [styles.navigationLink, isActive ? styles.navigationLinkActive : '']
                    .filter(Boolean)
                    .join(' ')
                }
              >
                {item.label}
              </NavLink>
            ))}
            {guardianAlphaItems.length > 0 && (
              <>
                <div className={styles.sectionTitle}>GuardianAlpha</div>
                {guardianAlphaItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.to === '/guardian-alpha/manual-operating'}
                    className={({ isActive }) =>
                      [
                        styles.navigationLink,
                        styles.nestedNavigationLink,
                        isActive ? styles.navigationLinkActive : '',
                      ].filter(Boolean).join(' ')
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
              </>
            )}
          </div>

          <div className={styles.accountSection}>
            {user && (
              <div style={{ fontSize: 12, color: '#9090b0', marginBottom: 8 }}>
                {user.display_name || user.username}
                <span
                  style={{
                    marginLeft: 6,
                    fontSize: 10,
                    background: '#2d2d50',
                    color: '#9090b0',
                    padding: '1px 6px',
                    borderRadius: 8,
                  }}
                >
                  {user.role}
                </span>
              </div>
            )}
            <NavLink
              to="/account/change-password"
              style={{
                ...sidebarAccountButtonStyle,
                marginBottom: 8,
              }}
            >
              Change password
            </NavLink>
            <button
              onClick={handleLogout}
              style={sidebarAccountButtonStyle}
            >
              Sign out
            </button>
            <div
              className={styles.version}
              title="dashboard / server build version"
            >
              <div>dashboard v{dashboardVersion}</div>
              <div>server v{serverVersion ?? '...'}</div>
            </div>
          </div>
        </nav>
      </aside>

      <div ref={contentShellRef} className={styles.contentShell}>
        <header className={styles.header}>
          {isCompact && (
            <button
              ref={sidebarTriggerRef}
              type="button"
              className={styles.menuButton}
              aria-label="Open navigation"
              aria-controls={SIDEBAR_ID}
              aria-expanded={isSidebarOpen}
              onClick={() => {
                pendingFocusRef.current = null
                setIsSidebarOpen(true)
              }}
            >
              <Menu size={20} aria-hidden="true" />
            </button>
          )}
          <AlertPanel />
        </header>

        <main ref={mainRef} tabIndex={-1} className={styles.main}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}

const sidebarAccountButtonStyle: React.CSSProperties = {
  boxSizing: 'border-box',
  display: 'block',
  width: '100%',
  padding: '6px 0',
  background: 'transparent',
  border: '1px solid #3d3d60',
  borderRadius: 4,
  color: '#9090b0',
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: 'inherit',
  lineHeight: 'normal',
  textAlign: 'center',
  textDecoration: 'none',
}
